/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Component, ElementRef, Inject, OnDestroy, ViewChild } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { TranslocoService } from '@ngneat/transloco';
import { Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import {
  GeneratedReport,
  GeneratedReportConditionalSection,
  GeneratedReportSection,
  GeneratedReportStandardSection,
  Organization,
} from '../../../../../../shared/models';
import { MemberRecord } from '../../../../../../shared/models/admin/members.model';
import { ChartContent, ChartLearner } from '../../../../../../shared/models/editor/chart-content.model';
import { DEFAULT_LANGUAGE_CODE } from '../../../../../../shared/services/languages/language.service';
import { UserAuthState } from '../../../../../../user-auth/store/user-auth.state';
import { DIAGNOSTICS_DATA_SERVICE, DiagnosticsDataService } from '../../../../services/data.service';

@Component({
  templateUrl: './report-dialog-360.component.html',
  styleUrls: ['./report-dialog-360.component.scss'],
})
export class DiagnosticsViewReportDialog360Component implements OnDestroy {
  @Select(UserAuthState.organizationDetails)
  organizationData$: Observable<Organization>;

  @ViewChild('reportPdf') reportPdf: ElementRef<HTMLElement>;

  completedData: Date;
  downloadInProgress = false;
  reportMode: string;
  report: GeneratedReport;
  reportSections: GeneratedReportSection[] = [];
  member: MemberRecord;
  learner: ChartLearner;

  private currentLanguage = DEFAULT_LANGUAGE_CODE;
  private readonly assessmentUid: string;
  private readonly responsesUid: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { assessmentUid: string; responsesUid: string; memberData: MemberRecord },
    @Inject(DIAGNOSTICS_DATA_SERVICE) private diagnosticsDataService: DiagnosticsDataService,
    private translocoService: TranslocoService,
    private dialogRef: MatDialogRef<DiagnosticsViewReportDialog360Component>,
  ) {
    if (data) {
      this.member = data.memberData;
      this.learner = { imageUrl: this.member?.thumbnail, firstName: this.member?.about?.firstName };
      this.assessmentUid = data.assessmentUid;
      this.responsesUid = data.responsesUid;
      this.getReport(null);
    }
  }

  sectionTitle(section: GeneratedReportSection): string {
    switch (section.type) {
      case 'STANDARD_SECTION':
        // eslint-disable-next-line no-case-declarations
        const tempElement = document.createElement('div');
        tempElement.innerText = '...';

        for (const sectionContent of (section as GeneratedReportStandardSection).dynamicContent) {
          if (typeof sectionContent.content === 'string') {
            tempElement.innerHTML = sectionContent.content;
            break;
          }
        }

        // eslint-disable-next-line no-case-declarations
        const content = tempElement.textContent || tempElement.innerText || '';

        if (content.length > 30) {
          return content.substring(0, 30) + '...';
        }

        return content;
      case 'FEEDBACK_PROVIDERS_SECTION':
        return this.translocoService.translate('translations.diagnostics.labels.feedbackProviders');
      case 'FEEDBACK_QUESTIONS_SECTION':
        return this.translocoService.translate('translations.diagnostics.labels.feedbackQuestions');
      case 'FEEDBACK_STATS_SECTION':
        return this.translocoService.translate('translations.diagnostics.labels.feedbackStats');
      case 'CONDITIONAL_SECTION':
        return (section as GeneratedReportConditionalSection).learningGoalWrapper.tagTitle;
      default:
        return '';
    }
  }

  scrollTo(section: GeneratedReportSection): void {
    const element = document.getElementById(section.uid);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  trackByFn(index: number, { uid }) {
    return uid || index;
  }

  back(): void {
    document.body.classList.remove('dialog-print');
    this.dialogRef.close();
  }

  downloadReport(): void {
    document.body.classList.add('dialog-print');
    window.print();
  }

  ngOnDestroy(): void {
    document.body.classList.remove('dialog-print');
  }

  onFilterProviders(data: string): void {
    this.getReport(data);
  }

  private setSectionChartColors(sections: GeneratedReportSection[]): void {
    const standardSections = sections.filter((item) => item.type === 'STANDARD_SECTION') as GeneratedReportStandardSection[];

    let chartContent = null;
    for (const section of standardSections) {
      for (const content of section.dynamicContent) {
        if (content.type === 'CHART') {
          chartContent = content;
          break;
        }
      }
      if (chartContent) {
        break;
      }
    }
    if (!chartContent) {
      return;
    }

    this.setConditionalSectionColors(chartContent, sections);
  }

  private setConditionalSectionColors(chartContent: ChartContent, sections: GeneratedReportSection[]): void {
    const colors = {};
    chartContent.items.map((item) => {
      colors[item.itemUid] = item.color;
    });
    sections.map((section) => {
      if (section.type === 'CONDITIONAL_SECTION') {
        (section as GeneratedReportConditionalSection).chartColor =
          colors[(section as GeneratedReportConditionalSection).learningGoalWrapper.tagUid];
      }
    });
  }

  private getReport(feedbackProviders?: string): void {
    this.diagnosticsDataService
      .getReport(this.assessmentUid, this.responsesUid, feedbackProviders, this.currentLanguage)
      .subscribe(({ isSuccess, value }) => {
        if (isSuccess && value) {
          this.report = value;
          this.reportMode = this.report.reportMode;
          this.reportSections = this.report.sections;
          this.setSectionChartColors(this.reportSections);
          this.completedData = new Date();
        }
      });
  }
}
